import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { HeadingText } from '@/components/atoms/HeadingText';
import { InputText } from '@/components/atoms/Inputs/InputText';
import { useFormContext } from '@/components/forms/FormContext';
import { Step } from '@/components/forms/Step';
import { LocatorMap } from '@/components/LocatorMap';
import { GoogleMaps } from '@/components/scripts/GoogleMaps';
import { useValidateStep } from '@/components/forms/useValidateStep';
import { initAutocomplete } from '@/helpers/initAutocomplete';
import sendUpdatestoProfile from '@/helpers/userProfile/updateAddress';
import {
	type ReplaceVirtuallyFormState,
	StepMap,
	steps,
} from '@/app/replace/virtual/components/ReplaceVirtuallyForm/constants';
import { AddressStepActions, isValid } from './state';
import { handleNext } from './routing';
import { HiddenInputs } from './HiddenInputs';
import { type ReplaceVirtuallyFormActionMap } from '@/app/replace/virtual/components/ReplaceVirtuallyForm/reducer';
import { SpaceWow } from '@/components/SpaceWow';
import { TextAlert } from '@/components/calculators/TextAlert';
import { IconInfoBubble } from '@/components/icons';
import getDealerCountForAddress from '@/helpers/getDealerCountForAddress';

export interface MapData {
	location: google.maps.LatLngLiteral | google.maps.LatLng;
	viewport?: google.maps.LatLngBounds;
}

export interface HiddenInputData {
	city: string;
	state: string;
	postcode: string;
}

export interface AddressStepProps {
	testId?: string;
}

const AddressStep = (props: AddressStepProps) => {
	const { testId = 'AddressStep' } = props;
	const { dispatch, state } = useFormContext<
		StepMap,
		ReplaceVirtuallyFormState,
		ReplaceVirtuallyFormActionMap
	>();
	useValidateStep<
		StepMap,
		ReplaceVirtuallyFormState,
		ReplaceVirtuallyFormActionMap
	>(isValid, steps.Address);
	const [mapContent, setMapContent] = useState<MapData | null>(null);
	const [autofillExtraInputs, setAutofillExtraInputs] =
		useState<HiddenInputData>({
			city: '',
			state: '',
			postcode: '',
		});
	const autocompleteRef = useRef<google.maps.places.Autocomplete>();
	// Map options
	const options = {
		componentRestrictions: { country: 'us' },
		fields: ['formatted_address', 'address_components', 'geometry'],
		types: ['address'],
		strictBounds: false,
	};

	const updateAddress = useCallback(
		(address: string) => {
			getDealerCountForAddress(address).then((count) => {
				dispatch({
					type: AddressStepActions.UPDATE_AVAILABLE_DEALER_COUNT,
					payload: count,
				});
			});
			dispatch({ type: AddressStepActions.UPDATE_ADDRESS, payload: address });
		},
		[dispatch],
	);

	const loadAutocomplete = () => {
		const addressInput = document.getElementById('address') as HTMLInputElement;
		initAutocomplete(setAutocomplete);

		async function setAutocomplete() {
			const { Autocomplete } = (await google.maps.importLibrary(
				'places',
			)) as google.maps.PlacesLibrary;

			if (!autocompleteRef.current) {
				autocompleteRef.current = new Autocomplete(
					addressInput as HTMLInputElement,
					options,
				);
			}

			autocompleteRef.current.addListener('place_changed', () => {
				const place = autocompleteRef.current?.getPlace();

				if (place) {
					updateAddress(
						place.formatted_address?.replace(', USA', '') as string,
					);

					if (place.address_components) {
						sendUpdatestoProfile(extractAddress(place.address_components));
					}

					setMapContent({
						location: {
							lat: place.geometry?.location?.lat() as number,
							lng: place.geometry?.location?.lng() as number,
						},
						viewport: place.geometry?.viewport,
					});
				}
			});
		}
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		dispatch({
			type: AddressStepActions.UPDATE_ADDRESS,
			payload: event.target.value,
		});

		if (!autocompleteRef.current) {
			loadAutocomplete();
		}
	};

	useEffect(() => {
		const getAddressInfo = async (fullAddress: string) => {
			const { Geocoder } = (await google.maps.importLibrary(
				'geocoding',
			)) as google.maps.GeocodingLibrary;

			const geocoder = new Geocoder();

			if (geocoder) {
				geocoder
					.geocode({ address: fullAddress })
					.then((result) => {
						const { results } = result;
						updateAddress(results[0].formatted_address.replace(', USA', ''));

						setMapContent({
							location: results[0].geometry.location,
							viewport: results[0].geometry.viewport,
						});

						setAutofillExtraInputs({
							city: '',
							state: '',
							postcode: '',
						});
					})
					.catch((e: string) => {
						console.log(e);
					});
			}
		};

		// This runs if the browser prompts for autofill
		if (
			autofillExtraInputs.city !== '' &&
			autofillExtraInputs.state !== '' &&
			autofillExtraInputs.postcode !== ''
		) {
			const fullAddress = `${state.address}, ${autofillExtraInputs.city}, ${autofillExtraInputs.state} ${autofillExtraInputs.postcode}`;

			sendUpdatestoProfile({
				city: autofillExtraInputs.city,
				state: autofillExtraInputs.state,
				postcode: autofillExtraInputs.postcode,
			});

			getAddressInfo(fullAddress);
		}
	}, [
		autofillExtraInputs.city,
		autofillExtraInputs.postcode,
		autofillExtraInputs.state,
		state.message,
		updateAddress,
	]);

	return (
		<div data-testid={testId}>
			<Step<StepMap, ReplaceVirtuallyFormState, ReplaceVirtuallyFormActionMap>
				name={steps.Address}
				onNext={handleNext}
			>
				<div className="mt-10 lg:mt-20">
					<HeadingText tag="h1" size="h2" balanced>
						Great, what&apos;s your home address?
					</HeadingText>

					<SpaceWow spacer_10 lg__spacer_14 />

					<div className="flex w-full flex-col items-center">
						<div className="flex w-full flex-col items-center">
							<InputText
								autoComplete="address-line1"
								id="address"
								name="address"
								label="Home address"
								helperText="e.g. 123 Happy Street, Charlotte, NC, 28202"
								fieldsetClassName="grid w-full max-w-101 mx-auto"
								inputClassName="w-full text-slate-500"
								onChange={handleChange}
								value={state.address}
								minLength={5}
								required
							/>

							{mapContent?.location && (
								<LocatorMap
									location={mapContent?.location as google.maps.LatLngLiteral}
									viewport={mapContent?.viewport}
								/>
							)}
						</div>
						<div className="max-w-2xl">
							<SpaceWow spacer_08 lg__spacer_10 />
							<TextAlert
								renderIcon={IconInfoBubble}
								text="Your address allows us to determine pricing specific to your region."
								title="Why are we asking?"
							/>
						</div>
					</div>
					<HiddenInputs
						setAutofillExtraInputs={setAutofillExtraInputs}
						autofillExtraInputs={autofillExtraInputs}
					/>
					<GoogleMaps />
				</div>
			</Step>
		</div>
	);
};

export default AddressStep;

function extractAddress(address: google.maps.GeocoderAddressComponent[]) {
	const city = address.find((o) => o.types.includes('locality'));
	const state = address.find((o) =>
		o.types.includes('administrative_area_level_1'),
	);
	const postcode = address.find((o) => o.types.includes('postal_code'));

	return {
		city: city?.long_name || '',
		state: state?.short_name || '',
		postcode: postcode?.long_name || '',
	};
}
